import React, { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload } from "lucide-react";
import { OrderStatus } from "@/models/Order";

const statusOptions = [
  { value: OrderStatus.draft, label: "Draft" },
  { value: OrderStatus.application_submitted, label: "Submitted" },
  { value: OrderStatus.under_review, label: "Under Review" },
  { value: OrderStatus.on_hold, label: "On Hold" },
  { value: OrderStatus.approved, label: "Approved" },
  { value: OrderStatus.rejected, label: "Rejected" },
  { value: OrderStatus.cancelled, label: "Cancelled" }
];

export interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (status: string, note: string, notifyCustomer: boolean, file?: File | null) => Promise<void>;
  currentStatus: OrderStatus | string;
  notifyCustomer?: boolean;
  uploading?: boolean;
  file?: File | null;
  setFile?: (file: File | null) => void;
}

export const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  currentStatus,
  notifyCustomer: initialNotifyCustomer = false,
  uploading = false,
  file,
  setFile,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | "">(currentStatus as OrderStatus || "");
  const [note, setNote] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(initialNotifyCustomer);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedStatus) return;
    setIsSubmitting(true);
    await onUpdate(selectedStatus as OrderStatus, note, notifyCustomer, file);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setSelectedStatus(currentStatus as OrderStatus || "");
    setNote("");
    setNotifyCustomer(initialNotifyCustomer);
    if (setFile) setFile(null);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!setFile) return;
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) handleClose();
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Order Status</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label htmlFor="status" className="text-sm font-medium">Status</label>
            <Select
              value={selectedStatus}
              onValueChange={(value: string) => setSelectedStatus(value as OrderStatus)}
            >
              <SelectTrigger id="status" className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="note" className="text-sm font-medium">Note (Optional)</label>
            <Textarea
              id="note"
              placeholder="Add a note about this status change"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-1">
              <Upload className="h-4 w-4" /> Attachment (Optional)
            </label>
            <input
              id="status-attachment"
              type="file"
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              onChange={handleFileChange}
              disabled={uploading || isSubmitting}
            />
            {file && <div className="text-xs text-muted-foreground">Selected: {file.name}</div>}
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="notify-customer"
              checked={notifyCustomer}
              onCheckedChange={(checked) => setNotifyCustomer(checked === true)}
            />
            <label
              htmlFor="notify-customer"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Notify customer about this status change via email
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !selectedStatus ||
              selectedStatus === currentStatus ||
              isSubmitting ||
              uploading
            }
          >
            {(isSubmitting || uploading) ? "Updating..." : "Update Status"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
